'use client';

import { create } from 'zustand';
import { api } from '@/shared/lib/api';

/** Fase da execução de um questionário: gerar respostas ou, já com as
 * respostas geradas, calcular a confiança de cada uma (ver `RunPhase` no
 * backend e `confidence_service.py`). */
export type RunPhase = 'answering' | 'confidence';

export type RunStatus =
  | 'idle'
  | 'running'
  | 'completed'
  | 'completed_partial'
  | 'completed_with_errors'
  | 'error';

/** Um processo em curso (ou recém-terminado) dentro de um projeto: UM
 * questionário numa UMA fase.
 *
 * A granularidade é (questionário, fase) porque os processos de um mesmo
 * projeto coexistem no tempo: a confiança de um questionário já respondido é
 * calculada — noutra réplica — enquanto outro questionário ainda está a ser
 * respondido, e numa execução de "todos" há vários questionários em voo. Um
 * único registo por projeto fazia a mensagem que chegasse por último apagar
 * a barra do outro processo. */
interface QuestionnaireRunEntry {
  /** `null` só na entrada agregada de uma execução de "todos" que ainda não
   * recebeu nenhuma mensagem de um questionário concreto. */
  questionnaireId: string | null;
  questionnaireName?: string;
  phase: RunPhase;
  progress: number;
  currentQuestion: number;
  totalQuestions: number;
  message: string | null;
  error: string | null;
  status: Exclude<RunStatus, 'idle'>;
  /** Trabalho desta fase ainda por fechar, tal como a última mensagem de
   * progresso o reportou (ver `open_count` em
   * `WebSocketManager.send_questionnaire_progress`). `null` enquanto nenhuma
   * mensagem o trouxer — arranques locais (`startRun`) e backends anteriores
   * ao campo.
   *
   * A `0` com `status === 'running'` é um processo DRENADO: a fase já não tem
   * nada aberto, mas a confirmação (`questionnaire_complete`) ainda não
   * chegou — ou perdeu-se. Não conta como a correr nem como concluído (ver
   * `isDrainedEntry`): a barra desaparece de imediato, sem anunciar um
   * desfecho que ninguém confirmou. */
  openCount: number | null;
  /** true entre o clique em "cancelar" e a confirmação (WS) de que o
   * processo parou. */
  isCancelling: boolean;
  /** erro ao pedir o cancelamento (ex. falha de rede) — distinto de `error`. */
  cancelError: string | null;
}

interface QuestionnaireRunProjectState {
  projectName?: string;
  /** Execução de "todos os questionários" pedida a partir deste cliente —
   * mantém o projeto "a correr" na janela entre o pedido e a primeira
   * mensagem de progresso, e entre um questionário e o seguinte. */
  isRunningAll: boolean;
  /** Os processos deste projeto, por `runKey` — ordem de inserção, que é a
   * ordem cronológica em que apareceram. */
  runs: Record<string, QuestionnaireRunEntry>;
}

/** Chave de um processo dentro do projeto. A fase faz parte da chave: é o
 * que permite ver as duas barras ao mesmo tempo. */
const runKey = (questionnaireId: string | null | undefined, phase: RunPhase) =>
  `${questionnaireId ?? 'all'}:${phase}`;

/** Entrada agregada de uma execução de "todos" sem questionário conhecido. */
const RUN_ALL_KEY = runKey(null, 'answering');

const createIdleProjectState = (): QuestionnaireRunProjectState => ({
  isRunningAll: false,
  runs: {},
});

/** Nome do questionário tal como já é conhecido por outro processo do mesmo
 * projeto: só o arranque local (`startRun`) o recebe, mas as mensagens WS de
 * uma fase seguinte devem identificar o mesmo questionário no popup. */
const inheritQuestionnaireName = (
  project: QuestionnaireRunProjectState,
  questionnaireId: string | null | undefined,
): string | undefined => {
  if (!questionnaireId) return undefined;
  for (const entry of Object.values(project.runs)) {
    if (entry.questionnaireId === questionnaireId && entry.questionnaireName) {
      return entry.questionnaireName;
    }
  }
  return undefined;
};

const createRunEntry = (
  questionnaireId: string | null,
  phase: RunPhase,
  questionnaireName?: string,
): QuestionnaireRunEntry => ({
  questionnaireId,
  questionnaireName,
  phase,
  progress: 0,
  currentQuestion: 0,
  totalQuestions: 0,
  message: null,
  error: null,
  status: 'running',
  openCount: null,
  isCancelling: false,
  cancelError: null,
});

/** Estados terminais de um processo: um desfecho já confirmado pelo
 * backend. */
const isFinishedStatus = (status: RunStatus | undefined) =>
  status === 'completed' ||
  status === 'completed_partial' ||
  status === 'completed_with_errors' ||
  status === 'error';

/** Processo cuja fase já não tem trabalho aberto mas cujo desfecho ainda não
 * foi confirmado — ver `openCount`. Invisível: nem barra, nem pill, nem
 * banner. */
export const isDrainedEntry = (entry: { status: RunStatus; openCount: number | null }) =>
  entry.status === 'running' && entry.openCount === 0;

/** A fase a que esta mensagem pertence tem trabalho aberto AGORA?
 *
 * É a única fonte desta decisão, e vem do backend (`open_count`, ver
 * `WebSocketManager.send_questionnaire_progress`). Substituiu uma janela
 * temporal ("ignorar progresso nos N segundos seguintes a uma conclusão"),
 * que errava nos dois sentidos: engolia a reabertura legítima da fase de
 * respostas — repetir perguntas falhadas, ou perguntas novas, com a fase de
 * confiança já a correr — e, passada a janela, deixava um heartbeat
 * atrasado (0.5s, ver `heartbeat_loop`) ressuscitar uma barra de uma fase
 * que já tinha acabado, sem nada que a voltasse a fechar.
 *
 * Sem o campo (backend anterior a ele) cai na percentagem: abaixo de 100%
 * assume-se trabalho aberto. */
const messageHasOpenWork = (openCount: number | null, percent: number) =>
  openCount === null ? percent < 100 : openCount > 0;

// Timeout defensivo: se nenhuma atualização WS confirmar o cancelamento
// entretanto (ex. NOTIFY perdido porque a réplica dona reiniciou), repõe o
// botão em vez de o deixar preso a girar indefinidamente.
const CANCEL_TIMEOUT_MS = 20000;
const cancelRequestTimers = new Map<string, ReturnType<typeof setTimeout>>();

const DISMISS_DELAY_MS = 5500;
const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Rede de último recurso para um processo que emudece: o browser perde a
// ligação (ou o NOTIFY perde-se) exatamente na mensagem que o fecharia, e a
// barra ficaria a girar para sempre — nenhuma mensagem futura lhe diz
// respeito. Não é o caminho normal de fecho (esse é o `open_count`/
// `questionnaire_complete`, imediatos): daí a folga enorme face ao ritmo
// real de emissão — 0.5s no heartbeat da fase de respostas (ver
// `heartbeat_loop`) e uma mensagem por resposta na de confiança. Rearmado a
// cada mensagem do processo.
const RUN_SILENCE_TIMEOUT_MS = 15 * 60 * 1000;
const silenceTimers = new Map<string, ReturnType<typeof setTimeout>>();

const timerKey = (projectId: string, key: string) => `${projectId}|${key}`;

const clearCancelTimer = (projectId: string, scope: string) => {
  const id = timerKey(projectId, scope);
  const existing = cancelRequestTimers.get(id);
  if (existing) clearTimeout(existing);
  cancelRequestTimers.delete(id);
};

/** Cancelar limpa os timers de TODOS os âmbitos do projeto: uma confirmação
 * que chegue por qualquer processo dá o pedido por respondido. */
const clearProjectCancelTimers = (projectId: string) => {
  const prefix = `${projectId}|`;
  for (const id of Array.from(cancelRequestTimers.keys())) {
    if (id.startsWith(prefix)) {
      clearTimeout(cancelRequestTimers.get(id)!);
      cancelRequestTimers.delete(id);
    }
  }
};

const clearDismissTimer = (projectId: string, key: string) => {
  const id = timerKey(projectId, key);
  const existing = dismissTimers.get(id);
  if (existing) clearTimeout(existing);
  dismissTimers.delete(id);
};

const scheduleDismiss = (
  projectId: string,
  key: string,
  dismiss: (projectId: string, key?: string) => void,
) => {
  clearDismissTimer(projectId, key);
  const id = timerKey(projectId, key);
  dismissTimers.set(
    id,
    setTimeout(() => {
      dismissTimers.delete(id);
      dismiss(projectId, key);
    }, DISMISS_DELAY_MS),
  );
};

const clearSilenceTimer = (projectId: string, key: string) => {
  const id = timerKey(projectId, key);
  const existing = silenceTimers.get(id);
  if (existing) clearTimeout(existing);
  silenceTimers.delete(id);
};

/** (Re)arma a rede de último recurso deste processo — ver
 * `RUN_SILENCE_TIMEOUT_MS`. */
const armSilenceTimer = (
  projectId: string,
  key: string,
  dismiss: (projectId: string, key?: string) => void,
) => {
  clearSilenceTimer(projectId, key);
  const id = timerKey(projectId, key);
  silenceTimers.set(
    id,
    setTimeout(() => {
      silenceTimers.delete(id);
      dismiss(projectId, key);
    }, RUN_SILENCE_TIMEOUT_MS),
  );
};


/** Pior desfecho ganha: um erro num processo não pode ser tapado pela
 * conclusão limpa de outro do mesmo projeto. */
const TERMINAL_STATUS_PRIORITY: RunStatus[] = [
  'error',
  'completed_with_errors',
  'completed_partial',
  'completed',
];

export interface ProjectRunSummary {
  /** Há pelo menos um processo a correr neste projeto. */
  isRunning: boolean;
  isRunningAll: boolean;
  /** Estado do projeto como um todo: 'running' enquanto sobrar algum
   * processo, senão o pior desfecho dos que ainda não foram descartados. */
  status: RunStatus;
  /** Fase a mostrar em cada questionário a correr. Um questionário pode ter
   * as duas fases abertas (ex. repetir respostas falhadas enquanto a
   * confiança das restantes é calculada) — nesse caso ganha a de respostas,
   * que é a que ainda produz conteúdo novo na linha. */
  phaseByQuestionnaireId: Record<string, RunPhase>;
  runningQuestionnaireIds: string[];
}

const EMPTY_SUMMARY: ProjectRunSummary = {
  isRunning: false,
  isRunningAll: false,
  status: 'idle',
  phaseByQuestionnaireId: {},
  runningQuestionnaireIds: [],
};

/** Agrega os processos de um projeto na vista "por projeto" de que a página
 * precisa (banners, pills, botões). Pura — usar com `useMemo` sobre
 * `projectStatusById[projectId]`. */
export const summarizeProjectRuns = (
  project: QuestionnaireRunProjectState | undefined,
): ProjectRunSummary => {
  if (!project) return EMPTY_SUMMARY;

  const phaseByQuestionnaireId: Record<string, RunPhase> = {};
  const runningQuestionnaireIds: string[] = [];
  let isRunning = false;
  // Alguma entrada está drenada (ver `isDrainedEntry`): trabalho fechado,
  // desfecho ainda por confirmar. Sem isto, um processo cuja ÚNICA entrada
  // acaba de drenar sintetiza `status: 'idle'` por um instante — nem
  // `isRunning` nem `terminal` a contam — antes de o `questionnaire_complete`
  // real chegar a confirmar. Essa oscilação idle→running é invisível no ecrã
  // (nem pill nem banner mudam, ver `isDrainedEntry`), mas o efeito em
  // `useProjectOverviewPage` que refresca a lista ao SAIR de 'running'
  // (`prevRunStatusRef`) já a apanha nesse instante — e, quando a confirmação
  // real chega a seguir, já não há transição de 'running' para detetar, e a
  // lista fica com o estado antigo (ex.: questionário preso em "A
  // responder", só corrigido com um reload). Contar como `running` aqui,
  // mesmo sem pill nem banner, evita essa oscilação fantasma.
  let hasDrained = false;
  let terminal: RunStatus | null = null;

  for (const entry of Object.values(project.runs)) {
    if (isDrainedEntry(entry)) {
      hasDrained = true;
      continue;
    }
    if (entry.status === 'running') {
      isRunning = true;
      if (entry.questionnaireId) {
        if (!(entry.questionnaireId in phaseByQuestionnaireId)) {
          runningQuestionnaireIds.push(entry.questionnaireId);
        }
        // 'answering' tem precedência sobre 'confidence' (ver doc de
        // `phaseByQuestionnaireId`).
        if (
          phaseByQuestionnaireId[entry.questionnaireId] !== 'answering'
        ) {
          phaseByQuestionnaireId[entry.questionnaireId] = entry.phase;
        }
      }
      continue;
    }
    if (
      terminal === null ||
      TERMINAL_STATUS_PRIORITY.indexOf(entry.status) <
        TERMINAL_STATUS_PRIORITY.indexOf(terminal)
    ) {
      terminal = entry.status;
    }
  }

  // `isRunningAll` mantém o projeto "a correr" mesmo sem nenhum processo
  // ativo: entre o pedido e a primeira mensagem, e no intervalo entre o fim
  // de um questionário e o arranque do seguinte. Sem isto, o banner de
  // conclusão piscava a meio de uma execução de "todos".
  const status: RunStatus =
    isRunning || project.isRunningAll || hasDrained ? 'running' : (terminal ?? 'idle');

  return {
    isRunning: isRunning || project.isRunningAll || hasDrained,
    isRunningAll: project.isRunningAll,
    status,
    phaseByQuestionnaireId,
    runningQuestionnaireIds,
  };
};

/** Só o estado agregado, como primitivo — selector estável para quem apenas
 * reage à transição de 'running' para um desfecho. */
export const selectProjectRunStatus = (
  project: QuestionnaireRunProjectState | undefined,
): RunStatus => summarizeProjectRuns(project).status;

interface QuestionnaireRunState {
  projectStatusById: Record<string, QuestionnaireRunProjectState>;

  startRun: (
    projectId: string,
    id: string,
    projectName?: string,
    questionnaireName?: string,
    /** Fase que arranca. 'answering' por omissão (o caso normal: gerar as
     * respostas, a que a confiança se segue sozinha). 'confidence' quando o
     * utilizador pede SÓ o cálculo de confiança das respostas que ficaram
     * sem ele (ver `api.calculateQuestionnaireConfidence`): sem isto, a barra
     * aberta era a da fase de respostas, que nenhuma mensagem de progresso
     * dessa execução alimenta — ficava parada em 0% até expirar. */
    phase?: RunPhase,
  ) => void;
  startRunAll: (projectId: string, projectName?: string) => void;
  updateProgress: (data: {
    project_id: string;
    questionnaire_id: string;
    percent: number;
    message?: string;
    code?: string | null;
    /** Fase a que este progresso pertence, tal como o backend a envia (ver
     * `RunPhase`/`send_questionnaire_progress`). Ausente só em mensagens de
     * um backend anterior a este campo — assume-se a fase de respostas. */
    phase?: RunPhase;
    /** Trabalho desta fase ainda por fechar (ver `open_count` em
     * `send_questionnaire_progress`): é o que decide se esta mensagem abre,
     * mantém ou fecha a barra da fase. */
    open_count?: number | null;
    /** O `open_count` acima foi lido da BD no instante desta mensagem (ver
     * `open_count_fresh` em `send_questionnaire_progress`). `false` no
     * heartbeat da fase de respostas entre refrescos, que reemite a última
     * contagem conhecida. Ausente (backend anterior ao campo, ou emissores
     * que contam em memória) conta como fresco. */
    open_count_fresh?: boolean;
    current_question: number;
    total_questions: number;
    project_name?: string;
  }) => void;
  completeRun: (
    projectId: string,
    projectName?: string,
    hadFailure?: boolean,
    /** Execução interrompida a meio com respostas parciais (questionário
     * PARTIALLY_PROCESSED) — nem sucesso nem erro. */
    partial?: boolean,
    /** Fase que terminou (ver `phase` em `send_questionnaire_complete`). */
    phase?: RunPhase,
    /** Questionário que terminou. Sem ele não é possível saber qual dos
     * processos do projeto fechar. */
    questionnaireId?: string | null,
  ) => void;
  completeRunAll: (projectId: string, projectName?: string) => void;
  failRun: (
    projectId: string,
    error: string,
    projectName?: string,
    /** Questionário em erro; sem ele o erro é do projeto (ex. falha do
     * pedido de "correr todos") e encerra todos os seus processos. */
    questionnaireId?: string | null,
  ) => void;
  /** Descarta um processo (`key`, ver `runKey`) ou, sem `key`, todos os do
   * projeto. */
  dismissRun: (projectId: string, key?: string) => void;
  cancelRun: (
    projectId: string,
    /** Quando indicado, cancela só este questionário em vez de toda a
     * execução do projeto (ver api.cancelSingleQuestionnaireRun). */
    questionnaireId?: string | null,
  ) => Promise<void>;
  resetProject: (projectId: string) => void;
  getProjectStatus: (projectId: string) => QuestionnaireRunProjectState;
}

export const useQuestionnaireRunStore = create<QuestionnaireRunState>((set, get) => ({
  projectStatusById: {},

  startRun: (projectId, id, projectName?, questionnaireName?, phase: RunPhase = 'answering') => {
    const key = runKey(id, phase);
    clearDismissTimer(projectId, key);
    armSilenceTimer(projectId, key, get().dismissRun);
    set((state) => {
      const current = state.projectStatusById[projectId] ?? createIdleProjectState();
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...current,
            projectName: projectName ?? current.projectName,
            runs: {
              ...current.runs,
              [key]: createRunEntry(id, phase, questionnaireName),
            },
          },
        },
      };
    });
  },

  startRunAll: (projectId, projectName?) => {
    clearDismissTimer(projectId, RUN_ALL_KEY);
    // `isRunningAll` não tem trabalho aberto próprio por onde se fechar (ver
    // `open_count`): quem o encerra é o `questionnaire_run_all_complete`. Se
    // esse se perder, é esta rede que o apaga em vez de deixar uma barra
    // agregada para sempre — rearmada a cada mensagem do projeto.
    armSilenceTimer(projectId, RUN_ALL_KEY, get().dismissRun);
    set((state) => {
      const current = state.projectStatusById[projectId] ?? createIdleProjectState();
      // Os processos anteriores (já terminados) saem: o que se segue é uma
      // execução nova, e mantê-los só deixaria banners de desfechos velhos.
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            projectName: projectName ?? current.projectName,
            isRunningAll: true,
            runs: {},
          },
        },
      };
    });
  },

  updateProgress: (data) => {
    // Progresso deste questionário confirma que ele continua vivo: dá o
    // pedido de cancelamento por respondido no seu âmbito, mas NÃO no do
    // projeto — a rede defensiva dos outros processos (ver
    // `CANCEL_TIMEOUT_MS`) tem de continuar armada.
    clearCancelTimer(data.project_id, data.questionnaire_id);
    const phase = data.phase ?? 'answering';
    const key = runKey(data.questionnaire_id, phase);
    const current = get().projectStatusById[data.project_id];
    const entry = current?.runs[key];
    const openCount = data.open_count ?? null;
    const hasOpenWork = messageHasOpenWork(openCount, data.percent);
    // O `open_count` desta mensagem foi contado agora, ou é uma leitura
    // reutilizada de há alguns segundos? Ver `open_count_fresh` em
    // `send_questionnaire_progress`.
    //
    // Uma contagem reutilizada continua a servir para o que o heartbeat
    // existe — dizer que a fase está viva e mover a barra —, mas não pode
    // decidir NADA que dependa de ela ser do instante: abrir, reabrir ou
    // fechar a fase, e desligar o clamp monotónico. Com várias réplicas no
    // mesmo questionário, cada uma refresca a sua contagem num instante
    // diferente, e a que ainda tem a antiga (maior, porque outra réplica já
    // fechou trabalho entretanto) emite depois da que já refrescou: lida
    // como autoritativa, esse aumento passava por trabalho novo e a barra
    // recuava, para voltar a subir no tick seguinte.
    const openCountIsFresh = data.open_count_fresh !== false;

    // Uma mensagem sem trabalho aberto é o eco final da fase: não cria uma
    // barra nova (não há nada a mostrar — a fase acabou), nem reabre um
    // desfecho já confirmado. É este ramo que impede o heartbeat atrasado de
    // uma réplica de ressuscitar a barra das respostas depois de a fase
    // fechar — a barra a mais que ficava a girar sem nunca ter quem a
    // fechasse.
    //
    // Com contagem reutilizada o mesmo vale para o sentido oposto: sem
    // barra (ou com um desfecho já confirmado), só uma contagem do instante
    // pode abrir ou reabrir a fase. O evento que a reabre de verdade
    // (enfileirar trabalho novo, arrancar um lote) relê sempre a BD, pelo
    // que não se perde nenhuma reabertura legítima — no limite, atrasa-se
    // até ao próximo refresco.
    if ((!hasOpenWork || !openCountIsFresh) && (!entry || isFinishedStatus(entry.status))) {
      return;
    }
    // Com trabalho aberto, a mensagem manda: a fase está em curso agora,
    // mesmo que a tenhamos dado por encerrada (repetir perguntas falhadas, ou
    // perguntas novas, reabrem a fase de respostas com a de confiança já a
    // correr). Reabrir é começar de novo — a percentagem persistida foi
    // rebaselinada para o novo total (ver `TaskRepository._rebaseline_progress`),
    // pelo que o clamp monotónico tem de ser reposto em vez de manter a
    // barra colada aos 100% da conclusão anterior.
    const reopens = entry !== undefined && isFinishedStatus(entry.status);
    // O mesmo rebaseline acontece sem a fase ter chegado a fechar (retry a
    // meio): mais trabalho aberto do que na mensagem anterior significa
    // total novo, e a percentagem pode legitimamente descer. Só com a
    // contagem do instante, porém — ver `openCountIsFresh`: é exatamente
    // aqui que uma contagem reutilizada de outra réplica desligava o clamp
    // sem que nada tivesse crescido.
    const workGrew =
      openCountIsFresh &&
      openCount !== null &&
      entry?.openCount != null &&
      openCount > entry.openCount;
    if (current?.isRunningAll) {
      // Qualquer mensagem do projeto conta como sinal de vida da execução de
      // "todos" (ver `armSilenceTimer` em `startRunAll`).
      armSilenceTimer(data.project_id, RUN_ALL_KEY, get().dismissRun);
    }
    if (hasOpenWork || !openCountIsFresh) {
      // A mensagem é sinal de vida do processo em qualquer dos casos. Com
      // contagem reutilizada fica-se por aqui: um `open_count` de há alguns
      // segundos a dizer zero não é prova de que a fase drenou — pode ter
      // sido reaberta desde então, e quem fecha de verdade é a mensagem
      // seguinte que releu a BD (ou o `questionnaire_complete`).
      clearDismissTimer(data.project_id, key);
      armSilenceTimer(data.project_id, key, get().dismissRun);
    } else {
      // Fase drenada (chegados aqui, a entrada existe e estava a correr): a
      // barra desaparece já — `isDrainedEntry` tira-a de circulação —, e a
      // entrada fica só o tempo de o `questionnaire_complete` chegar com o
      // desfecho para o banner. Se se perder, o dismiss limpa-a; se chegar
      // depois disso, `completeRun` recria-a com o desfecho certo.
      clearSilenceTimer(data.project_id, key);
      scheduleDismiss(data.project_id, key, get().dismissRun);
    }
    set((state) => {
      const project = state.projectStatusById[data.project_id] ?? createIdleProjectState();
      const run =
        project.runs[key] ??
        createRunEntry(
          data.questionnaire_id,
          phase,
          inheritQuestionnaireName(project, data.questionnaire_id),
        );
      const message = data.message?.trim() ? data.message : run.message;
      // O cancelamento é pedido por questionário, não por fase: uma
      // mensagem de progresso de qualquer fase dele desmarca-o em todas.
      const runs = { ...project.runs };
      for (const [otherKey, entry] of Object.entries(runs)) {
        if (otherKey !== key && entry.questionnaireId === data.questionnaire_id && entry.isCancelling) {
          runs[otherKey] = { ...entry, isCancelling: false };
        }
      }
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [data.project_id]: {
            ...project,
            projectName: data.project_name ?? project.projectName,
            runs: {
              ...runs,
              [key]: {
                ...run,
                isCancelling: false,
                // Clamp monotónico dentro do processo. O backend persiste a
                // percentagem com um GREATEST atómico
                // (`QuestionnaireRepository.bump_progress`, comum às duas
                // fases), mas o envio acontece fora da secção crítica que o
                // serializa e ainda passa por duas idas à BD + NOTIFY antes
                // de sair: produtores concorrentes do mesmo questionário
                // (heartbeat, loop principal, e lotes noutras réplicas)
                // podem ultrapassar-se nessa janela e entregar aqui um valor
                // mais antigo do que o já mostrado. Sem este clamp, a barra
                // recuava e voltava a avançar. Cada fase tem a SUA entrada,
                // pelo que a de confiança começa nos 0% sem ficar colada aos
                // 100% da de respostas.
                progress: reopens || workGrew ? data.percent : Math.max(run.progress, data.percent),
                currentQuestion: data.current_question,
                totalQuestions: data.total_questions,
                message,
                error: null,
                status: 'running',
                // Só contagens do instante ficam guardadas: é este valor que
                // `isDrainedEntry` lê e contra o qual a próxima mensagem
                // compara (`workGrew`), e uma leitura reutilizada
                // contaminaria as duas decisões.
                openCount: openCountIsFresh ? openCount : run.openCount,
              },
            },
          },
        },
      };
    });
  },

  completeRun: (
    projectId,
    projectName?,
    hadFailure = false,
    partial = false,
    phase = 'answering',
    questionnaireId?,
  ) => {
    clearCancelTimer(projectId, questionnaireId ?? 'all');
    const key = runKey(questionnaireId, phase);
    const status: Exclude<RunStatus, 'idle' | 'running'> = hadFailure
      ? 'completed_with_errors'
      : partial
        ? 'completed_partial'
        : 'completed';
    // O fim das respostas NÃO fecha a fase de confiança (nem o contrário):
    // são processos distintos, cada um com a sua entrada e o seu dismiss.
    // `reconcile_questionnaire_after_confidence` reenvia
    // `questionnaire_complete` com `phase="confidence"` quando essa fase
    // acaba.
    set((state) => {
      const project = state.projectStatusById[projectId] ?? createIdleProjectState();
      const run =
        project.runs[key] ??
        createRunEntry(
          questionnaireId ?? null,
          phase,
          inheritQuestionnaireName(project, questionnaireId),
        );
      const runs = { ...project.runs };
      // O fim da confiança é o fim da execução DESTE questionário de ponta a
      // ponta (é a última fase, ver `reconcile_questionnaire_after_confidence`):
      // qualquer processo dele que tenha sobrado por fechar — porque a
      // mensagem que o fecharia se perdeu — sai agora, em vez de ficar a
      // girar indefinidamente ao lado das barras reais.
      if (phase === 'confidence' && questionnaireId) {
        for (const [otherKey, entry] of Object.entries(runs)) {
          // Só os drenados: um processo com trabalho aberto continua vivo e
          // fica (repetir perguntas falhadas reabre a fase de respostas, que
          // pode muito bem sobreviver ao fim desta fase de confiança), e um
          // já terminal tem o seu próprio dismiss agendado.
          if (otherKey !== key && entry.questionnaireId === questionnaireId && isDrainedEntry(entry)) {
            clearDismissTimer(projectId, otherKey);
            clearSilenceTimer(projectId, otherKey);
            delete runs[otherKey];
          }
        }
      }
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...project,
            projectName: projectName ?? project.projectName,
            runs: {
              ...runs,
              [key]: {
                ...run,
                isCancelling: false,
                progress: 100,
                message: null,
                status,
              },
            },
          },
        },
      };
    });
    clearSilenceTimer(projectId, key);
    scheduleDismiss(projectId, key, get().dismissRun);
  },

  completeRunAll: (projectId, projectName?) => {
    clearProjectCancelTimers(projectId);
    clearSilenceTimer(projectId, RUN_ALL_KEY);
    const finishedKeys: string[] = [];
    set((state) => {
      const project = state.projectStatusById[projectId] ?? createIdleProjectState();
      const runs: Record<string, QuestionnaireRunEntry> = {};
      for (const [key, entry] of Object.entries(project.runs)) {
        runs[key] =
          entry.status === 'running'
            ? { ...entry, isCancelling: false, progress: 100, message: null, status: 'completed' }
            : entry;
        finishedKeys.push(key);
      }
      // Sem nenhum processo registado (todos já descartados, ou nenhuma
      // mensagem chegou a este cliente) fica ainda assim a entrada agregada,
      // para o banner de conclusão do projeto ter de onde vir.
      if (Object.keys(runs).length === 0) {
        runs[RUN_ALL_KEY] = {
          ...createRunEntry(null, 'answering'),
          progress: 100,
          status: 'completed',
        };
        finishedKeys.push(RUN_ALL_KEY);
      }
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...project,
            projectName: projectName ?? project.projectName,
            isRunningAll: false,
            runs,
          },
        },
      };
    });
    for (const key of finishedKeys) {
      clearSilenceTimer(projectId, key);
      scheduleDismiss(projectId, key, get().dismissRun);
    }
  },

  failRun: (projectId, error, projectName?, questionnaireId?) => {
    if (questionnaireId == null) {
      clearProjectCancelTimers(projectId);
      clearSilenceTimer(projectId, RUN_ALL_KEY);
    } else {
      clearCancelTimer(projectId, questionnaireId);
    }
    const finishedKeys: string[] = [];
    set((state) => {
      const project = state.projectStatusById[projectId] ?? createIdleProjectState();
      const runs = { ...project.runs };
      // Um erro do questionário derruba as duas fases: a de confiança não
      // corre sobre respostas que falharam.
      const affected = Object.entries(runs).filter(
        ([, entry]) =>
          entry.status === 'running' &&
          (questionnaireId == null || entry.questionnaireId === questionnaireId),
      );
      for (const [key, entry] of affected) {
        runs[key] = {
          ...entry,
          isCancelling: false,
          message: null,
          error,
          status: 'error',
        };
        finishedKeys.push(key);
      }
      if (affected.length === 0) {
        // Erro sem nenhum processo ativo a que se ligar (ex. o pedido HTTP
        // de arranque falhou antes de qualquer mensagem WS).
        const key = runKey(questionnaireId, 'answering');
        runs[key] = {
          ...(runs[key] ??
            createRunEntry(
              questionnaireId ?? null,
              'answering',
              inheritQuestionnaireName(project, questionnaireId),
            )),
          isCancelling: false,
          message: null,
          error,
          status: 'error',
        };
        finishedKeys.push(key);
      }
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...project,
            projectName: projectName ?? project.projectName,
            // Um erro de projeto (sem questionário) encerra a execução de
            // "todos"; o erro de um questionário isolado não.
            isRunningAll: questionnaireId == null ? false : project.isRunningAll,
            runs,
          },
        },
      };
    });
    for (const key of finishedKeys) {
      clearSilenceTimer(projectId, key);
      scheduleDismiss(projectId, key, get().dismissRun);
    }
  },

  dismissRun: (projectId, key?) =>
    set((state) => {
      const project = state.projectStatusById[projectId];
      if (!project) return state;
      if (key === undefined) {
        for (const existing of Object.keys(project.runs)) {
          clearDismissTimer(projectId, existing);
          clearSilenceTimer(projectId, existing);
        }
        clearSilenceTimer(projectId, RUN_ALL_KEY);
        return {
          projectStatusById: {
            ...state.projectStatusById,
            [projectId]: { ...project, isRunningAll: false, runs: {} },
          },
        };
      }
      clearDismissTimer(projectId, key);
      clearSilenceTimer(projectId, key);
      const { [key]: _removed, ...rest } = project.runs;
      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...project,
            // A entrada agregada e o flag são a mesma coisa (ver
            // `RUN_ALL_KEY`): descartar uma tem de apagar o outro, senão o
            // popup voltava a sintetizar a barra no render seguinte.
            isRunningAll: key === RUN_ALL_KEY ? false : project.isRunningAll,
            runs: rest,
          },
        },
      };
    }),

  resetProject: (projectId) => {
    const project = get().projectStatusById[projectId];
    for (const key of Object.keys(project?.runs ?? {})) {
      clearDismissTimer(projectId, key);
      clearSilenceTimer(projectId, key);
    }
    clearProjectCancelTimers(projectId);
    set((state) => ({
      projectStatusById: {
        ...state.projectStatusById,
        [projectId]: createIdleProjectState(),
      },
    }));
  },

  cancelRun: async (projectId: string, questionnaireId?: string | null) => {
    const scope = questionnaireId ?? 'all';
    clearCancelTimer(projectId, scope);

    /** Marca `isCancelling`/`cancelError` nos processos abrangidos por este
     * cancelamento: os do questionário indicado, ou todos quando é o projeto
     * inteiro a ser cancelado. */
    const markScope = (isCancelling: boolean, cancelError: string | null) =>
      set((state) => {
        const project = state.projectStatusById[projectId] ?? createIdleProjectState();
        const runs = { ...project.runs };
        for (const [key, entry] of Object.entries(runs)) {
          if (questionnaireId != null && entry.questionnaireId !== questionnaireId) continue;
          runs[key] = { ...entry, isCancelling, cancelError };
        }
        return {
          projectStatusById: {
            ...state.projectStatusById,
            [projectId]: { ...project, runs },
          },
        };
      });

    markScope(true, null);

    cancelRequestTimers.set(
      timerKey(projectId, scope),
      setTimeout(() => {
        cancelRequestTimers.delete(timerKey(projectId, scope));
        markScope(false, null);
      }, CANCEL_TIMEOUT_MS),
    );

    try {
      // Um projeto pode ter vários questionários a correr; cancelar a partir
      // do toast visa o questionário que essa entrada mostra. Só sem
      // `questionnaireId` (execução de "todos") é que se cancela o projeto
      // inteiro.
      if (questionnaireId) {
        await api.cancelSingleQuestionnaireRun(questionnaireId);
      } else {
        await api.cancelQuestionnaireRun(projectId);
      }
    } catch (error) {
      clearCancelTimer(projectId, scope);
      markScope(false, (error as Error).message);
    }
  },

  getProjectStatus: (projectId) =>
    get().projectStatusById[projectId] ?? createIdleProjectState(),
}));
